import type { Actions, Rule } from './ability'
import { getActiveAccount, getMsalInitialized } from '@/plugins/auth/msalConfig'

// DAY-37: the signed-in user's permissions as the API reports them (api/v1/Account/Permissions).
// The API checks every request anyway; these rules only decide which menus and pages are shown.
interface ModulePermission {
  module: string
  actions: string[]
}

const actionFor: Record<string, Actions> = {
  View: 'read',
  Create: 'create',
  Edit: 'update',
  Delete: 'delete',
  Manage: 'administer',
}

// Set once the rules were loaded in this browser session (login or the router guard).
export const PermissionsLoadedKey = 'daybright:permissionsLoaded'

export const toAbilityRules = (permissions: ModulePermission[]): Rule[] =>
  permissions.flatMap(p => p.actions
    .filter(a => a in actionFor)
    .map(a => ({ action: actionFor[a], subject: p.module })))

const MaxWaitMs = 10000

// On a page reload the router guard runs before the MSAL plugin finished initializing
// (same wait as plugins/signalr.ts).
const waitForMsal = async () => {
  for (let waited = 0; !getMsalInitialized() && waited < MaxWaitMs; waited += 100)
    await new Promise(resolve => setTimeout(resolve, 100))

  return getMsalInitialized()
}

const fetchPermissions = async (): Promise<Rule[] | null> => {
  if (!await waitForMsal() || !getActiveAccount())
    return null

  const { data, error } = await useApi<ModulePermission[]>('/Account/Permissions')
  if (error.value || !Array.isArray(data.value))
    return null

  return toAbilityRules(data.value)
}

// Loads the rules for the current user, or null when they couldn't be loaded. Callers then show
// no module (fail closed) and try again later instead of keeping an empty menu all session.
// Never waits more than ~MaxWaitMs, so a navigation can't hang on it.
export const loadAbilityRules = async (): Promise<Rule[] | null> => {
  try {
    const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), MaxWaitMs * 2))

    return await Promise.race([fetchPermissions(), timeout])
  }
  catch (err) {
    console.error(err)

    return null
  }
}
